import React, { useCallback, useRef, useState } from 'react';
import { Checkbox, Flex, Modal, Typography } from 'antd';
import type { WordSense } from '@lingoping/core';
import type { SenseChoiceNeeded } from '../services/wordCardService.js';
import { CHROMATIC_PALETTES } from '../theme/index.js';
import { GenderBadge } from './GenderBadge.js';

const { Text } = Typography;

const PALETTE_FOR: Record<string, keyof typeof CHROMATIC_PALETTES> = {
  der: 'masculine',
  die: 'feminine',
  das: 'neuter',
};

/** "Bank (Möbel)": the word with the short German hint that tells its meanings apart. */
export function wordWithHint(word: string, hint?: string | null): string {
  return hint ? `${word} (${hint})` : word;
}

/**
 * The hint after a word that has several meanings: " (Möbel)". Nothing for a word with one
 * meaning, so most cards look exactly as before.
 */
export const SenseHint: React.FC<{ hint?: string | null; style?: React.CSSProperties }> = ({
  hint,
  style,
}) =>
  hint ? (
    <span lang="de" data-sense-hint style={{ color: '#475569', fontWeight: 400, ...style }}>
      {' '}
      ({hint})
    </span>
  ) : null;

interface Asking {
  lemma: string;
  senses: WordSense[];
  have: string[];
  resolve: (keys: string[] | null) => void;
}

/**
 * The meaning ticked when the chooser opens: the one the word's sentence points at, else the most
 * common one the learner does not have.
 */
export function defaultChoice(senses: WordSense[], have: string[], suggested?: string): string[] {
  if (suggested !== undefined && !have.includes(suggested)) {
    if (senses.some((s) => s.sense_key === suggested)) return [suggested];
  }
  const first = senses.find((s) => !have.includes(s.sense_key));
  return first ? [first.sense_key] : [];
}

/**
 * Asks the learner which meaning(s) of a word they want to learn, when a typed word has several
 * (Docs/plans/WORD_SENSES_PLAN.md). `ask(error)` resolves with the chosen meanings' keys, or null
 * when the learner cancels; render `chooser` once in the page.
 */
export function useSenseChooser(): {
  ask: (needed: SenseChoiceNeeded) => Promise<string[] | null>;
  chooser: React.ReactNode;
} {
  const [asking, setAsking] = useState<Asking | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const current = useRef<Asking | null>(null);

  const ask = useCallback((needed: SenseChoiceNeeded) => {
    // A second question while one is open replaces it: the first is answered "cancelled".
    current.current?.resolve(null);
    return new Promise<string[] | null>((resolve) => {
      const next = { lemma: needed.lemma, senses: needed.senses, have: needed.have, resolve };
      current.current = next;
      setChosen(defaultChoice(needed.senses, needed.have, needed.suggested));
      setAsking(next);
    });
  }, []);

  const finish = (keys: string[] | null) => {
    current.current?.resolve(keys);
    current.current = null;
    setAsking(null);
  };

  const toggle = (key: string, on: boolean) =>
    setChosen((keys) => (on ? [...keys, key] : keys.filter((k) => k !== key)));

  const chooser = asking ? (
    <Modal
      open
      title={`“${asking.lemma}” has ${asking.senses.length} meanings`}
      okText={chosen.length > 1 ? `Add ${chosen.length} cards` : 'Add card'}
      okButtonProps={{ disabled: chosen.length === 0 }}
      onOk={() =>
        // In the order the meanings are listed, whatever the order they were ticked in.
        finish(asking.senses.map((s) => s.sense_key).filter((key) => chosen.includes(key)))
      }
      onCancel={() => finish(null)}
      destroyOnHidden
      // The toolbar popup is narrow.
      style={{ maxWidth: 'calc(100vw - 16px)' }}
    >
      <Flex vertical gap={4}>
        <Text style={{ color: '#475569' }}>
          Which do you want to learn? Each gets its own card.
        </Text>
        <Flex vertical gap={10} style={{ marginTop: 12 }} role="group" aria-label="Meanings">
          {asking.senses.map((sense) => {
            const mine = asking.have.includes(sense.sense_key);
            return (
              <Checkbox
                key={sense.sense_key}
                checked={mine || chosen.includes(sense.sense_key)}
                disabled={mine}
                onChange={(event) => toggle(sense.sense_key, event.target.checked)}
              >
                <Flex vertical gap={2}>
                  <Flex align="center" gap={6} wrap>
                    {sense.article && PALETTE_FOR[sense.article] && (
                      <GenderBadge
                        article={sense.article}
                        palette={CHROMATIC_PALETTES[PALETTE_FOR[sense.article]]}
                      />
                    )}
                    <Text strong lang="de">
                      {wordWithHint(asking.lemma, sense.hint)}
                    </Text>
                    <Text>· {sense.label}</Text>
                  </Flex>
                  <Text style={{ fontSize: 12, color: '#475569' }}>
                    {mine
                      ? 'Already in your cards'
                      : sense.plural
                        ? `Plural: ${sense.plural}`
                        : ' '}
                  </Text>
                </Flex>
              </Checkbox>
            );
          })}
        </Flex>
      </Flex>
    </Modal>
  ) : null;

  return { ask, chooser };
}
